import { useMemo, useState } from "react";

import { Stack } from "@mui/material";
import { startOfISOWeek } from "date-fns";

import {
  ALL_FILTER_VALUE,
  matchesAppointmentFilters,
  matchesAttendanceFilter,
} from "@/pages/attendance-list/AttendanceList.utils";
import { AttendanceListHeader } from "@/pages/attendance-list/components/header/AttendanceListHeader";
import { AttendanceTable } from "@/pages/attendance-list/components/table/AttendanceTable";
import { useAttendanceOverviewQuery } from "@/pages/attendance-list/hooks/useAttendanceOverviewQuery";

export function AttendanceList() {
  const [weekStart, setWeekStart] = useState<Date>(() => startOfISOWeek(new Date()));
  // Filters live in page state only: they survive a week change and reset on reload.
  const [selectedCounselorId, setSelectedCounselorId] = useState(ALL_FILTER_VALUE);
  const [selectedAppointmentPurposeKey, setSelectedAppointmentPurposeKey] = useState(ALL_FILTER_VALUE);
  const [selectedAttendancePurposeKey, setSelectedAttendancePurposeKey] = useState(ALL_FILTER_VALUE);
  const [selectedLocation, setSelectedLocation] = useState(ALL_FILTER_VALUE);
  const { data: rows, isPending, isError } = useAttendanceOverviewQuery(weekStart);

  const filteredRows = useMemo(() => {
    const allRows = rows ?? [];
    return allRows.filter(
      (row) =>
        (selectedCounselorId === ALL_FILTER_VALUE || row.counselorId === selectedCounselorId) &&
        matchesAppointmentFilters(row, selectedAppointmentPurposeKey, selectedLocation) &&
        matchesAttendanceFilter(row, selectedAttendancePurposeKey)
    );
  }, [rows, selectedCounselorId, selectedAppointmentPurposeKey, selectedAttendancePurposeKey, selectedLocation]);

  return (
    <Stack spacing={2} sx={{ height: "100%" }} pb={3}>
      {/* The header gets the unfiltered rows on purpose: its option lists describe the week, so they
          must not shrink to whatever the current selection left over. */}
      <AttendanceListHeader
        weekStart={weekStart}
        rows={rows ?? []}
        selectedCounselorId={selectedCounselorId}
        selectedAppointmentPurposeKey={selectedAppointmentPurposeKey}
        selectedAttendancePurposeKey={selectedAttendancePurposeKey}
        selectedLocation={selectedLocation}
        onWeekStartChange={setWeekStart}
        onSelectedCounselorIdChange={setSelectedCounselorId}
        onSelectedAppointmentPurposeKeyChange={setSelectedAppointmentPurposeKey}
        onSelectedAttendancePurposeKeyChange={setSelectedAttendancePurposeKey}
        onSelectedLocationChange={setSelectedLocation}
      />
      {/* Sized, not scrolled — the table's own TableContainer owns the scroll so its header can stick.
          An unresolved query yields no rows too, and "no active staff members" would be the wrong
          explanation for a pending or failed request. */}
      <Stack sx={{ flex: 1, minHeight: 0 }}>
        <AttendanceTable rows={filteredRows} weekStart={weekStart} showEmptyMessage={!isPending && !isError} />
      </Stack>
    </Stack>
  );
}
